import React, { useState } from 'react';
import { Mail, Lock, Loader2, AlertCircle, CheckCircle2, ArrowRight, KeyRound } from 'lucide-react';
import { AuthService, AuthSession } from '../services/authService';
import { LicenseManager } from '../utils/licenseManager';
import { Logo } from './Logo';
import { LicenseSelectionModal, LicenseOption } from './LicenseSelectionModal';

interface LoginProps {
  onLoginSuccess: (session: AuthSession) => void;
  onSwitchToRegister: () => void;
}

type LoginStep = 'email' | 'otp' | 'license-key' | 'license-select' | 'loading';

export const LoginScreen: React.FC<LoginProps> = ({ onLoginSuccess, onSwitchToRegister }) => {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [platform, setPlatform] = useState('');
  const [availableLicenses, setAvailableLicenses] = useState<LicenseOption[]>([]);
  const [transferWarning, setTransferWarning] = useState<{ message: string; lockDays: number } | null>(null);
  const [selectedLicenseForTransfer, setSelectedLicenseForTransfer] = useState<string | undefined>(undefined);

  React.useEffect(() => {
    const initDevice = async () => {
      const id = await LicenseManager.getMachineId();
      const plat = await LicenseManager.getPlatform();
      setDeviceId(id);
      setPlatform(plat);
    };
    initDevice();
  }, []);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await AuthService.sendOTP(email, false);
    if (result.success) {
      setMessage(result.message);
      setStep('otp');
    } else {
      setError(result.message);
    }
    setLoading(false);
    const handleConfirmTransfer = async () => {
      setLoading(true);
      setError('');
      setTransferWarning(null);

      // Retry login with confirmTransfer flag set to true
      const result = await AuthService.loginUser(
        email,
        otp,
        undefined,
        deviceId,
        platform,
        selectedLicenseForTransfer, // Will be undefined for initial login, or set for license selection
        true // confirmTransfer = true
      );

      if (result.success && result.session) {
        setMessage('Login successful!');
        setTimeout(() => {
          onLoginSuccess(result.session!);
        }, 1000);
      } else {
        setError(result.message || 'Failed to transfer license');
      }
      setLoading(false);
    };

    const handleCancelTransfer = () => {
      setTransferWarning(null);
      setSelectedLicenseForTransfer(undefined);
      setError('Device transfer cancelled');
    };

  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!deviceId || !platform) {
      setError('Device information not available');
      setLoading(false);
      return;
    }

    const result = await AuthService.loginUser(email, otp, undefined, deviceId, platform);
    
    if (result.success && result.session) {
      setMessage('Login successful!');
      setTimeout(() => {
        onLoginSuccess(result.session!);
      }, 1000);
    } else if (result.requiresTransferConfirmation) {
      // Device transfer requires confirmation
      setTransferWarning({ message: result.warning || '', lockDays: result.lockDays || 7 });
      setSelectedLicenseForTransfer(undefined);
      setError('');
    } else if (result.requiresSelection && result.availableLicenses) {
      // User has multiple licenses - show selection modal
      setAvailableLicenses(result.availableLicenses);
      setStep('license-select');
      setError('');
    } else if (result.trialExpired || result.noLicenses || result.licenseExpired) {
      // Trial expired, license expired, or no licenses - ask for license key
      setStep('license-key');
      setError('');
      setMessage(
        result.licenseExpired
          ? 'Your license has expired. Please enter a new license key to continue.'
          : result.trialPlatformMismatch
            ? result.message || 'Your trial is locked to another platform. Please enter a license key to use on this device.'
            : result.noLicenses
              ? 'No active licenses found. Please enter a license key to continue.'
              : 'Your trial has expired. Please enter a license key to continue.'
      );
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleLicenseSelected = async (selectedLicenseId: string) => {
    setLoading(true);
    setError('');

    console.log('🎯 User selected license:', selectedLicenseId);
    console.log('📧 Using email:', email);
    console.log('🔢 Using OTP:', otp);
    console.log('📱 Device:', deviceId, platform);

    // Retry login with the selected license
    const result = await AuthService.loginUser(email, otp, undefined, deviceId, platform, selectedLicenseId);
    
    console.log('🔄 License selection result:', result);
    
    if (result.success && result.session) {
      setMessage('Login successful!');
      setTimeout(() => {
        onLoginSuccess(result.session!);
      }, 1000);
    } else if (result.requiresTransferConfirmation) {
      // Device transfer requires confirmation for this license
      setTransferWarning({ message: result.warning || '', lockDays: result.lockDays || 7 });
      setSelectedLicenseForTransfer(selectedLicenseId);
      setError('');
    } else {
      console.error('❌ License selection failed:', result.message);
      setError(result.message || 'Failed to use selected license');
      setStep('otp'); // Go back to OTP step
    }
    setLoading(false);
  };

  const handleEnterLicenseKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      setLoading(false);
      return;
    }

    if (!deviceId || !platform) {
      setError('Device information not available');
      setLoading(false);
      return;
    }

    try {
      // Call activateLicense directly - OTP was already verified, don't need to verify again
      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/activateLicense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: licenseKey.trim(),
          email,
          deviceId,
          platform
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to activate license');
        setLoading(false);
        return;
      }

      // Success - create session and continue
      const session: AuthSession = {
        email,
        licenseNumber: data.licenseId || licenseKey.trim(),
        deviceId,
        platform,
        tier: data.tier || 'PROFESSIONAL',
        validUntil: data.validUntil || data.expiryDate,
        issuedAt: Date.now(),
        isValid: true
      };

      // Save session
      await AuthService.saveSession(session);

      setMessage('License activated successfully!');
      setTimeout(() => {
        onLoginSuccess(session);
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to activate license');
    }
    setLoading(false);
  };

  // Show transfer warning modal if needed
  if (transferWarning) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Device Transfer Warning</h2>
            <p className="text-gray-600">{transferWarning.message}</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800 font-medium">
              ⚠️ You will be locked to this device for {transferWarning.lockDays} days
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              You cannot switch to another device during this time.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleConfirmTransfer}
              disabled={loading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Confirm Transfer
                </>
              )}
            </button>
            <button
              onClick={handleCancelTransfer}
              disabled={loading}
              className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show license selection modal if needed
  if (step === 'license-select' && availableLicenses.length > 0) {
    return (
      <LicenseSelectionModal
        licenses={availableLicenses}
        onSelect={handleLicenseSelected}
        onCancel={() => {
          setStep('otp');
          setAvailableLicenses([]);
        }}
        loading={loading}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Logo darkMode={true} className="h-16 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">Sign In</h1>
            <p className="text-slate-400 text-sm">License Plate Designer</p>
          </div>

          {/* Step: Email Input */}
          {step === 'email' && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Login Code
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onSwitchToRegister}
                className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium py-3 rounded-lg transition-colors"
              >
                New Customer? Register
              </button>
            </form>
          )}

          {/* Step: OTP Input */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="text-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">
                  Check your email for a verification code
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError('');
                  }}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Enter the 6-digit code sent to {email}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {message && (
                <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-400">{message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setError('');
                  setMessage('');
                }}
                className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium py-3 rounded-lg transition-colors"
              >
                Back
              </button>
            </form>
          )}

          {/* Step: License Key for Expired Trial */}
          {step === 'license-key' && (
            <form onSubmit={handleEnterLicenseKey} className="space-y-4">
              <div className="text-center mb-6">
                <KeyRound className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-slate-300 text-sm font-semibold">
                  Enter Your License Key
                </p>
              </div>

              {message && (
                <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-yellow-400">{message}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  License Key
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={licenseKey}
                    onChange={(e) => {
                      setLicenseKey(e.target.value);
                      setError('');
                    }}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !licenseKey}
                className="w-full bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    Activate License
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('otp');
                  setLicenseKey('');
                  setError('');
                  setMessage('');
                }}
                className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium py-3 rounded-lg transition-colors"
              >
                Back
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-700 text-center text-xs text-slate-500">
            <p>
              By signing in, you agree to our{' '}
              <a href="#" className="text-blue-400 hover:text-blue-300">
                Terms
              </a>
              {' '}and{' '}
              <a href="#" className="text-blue-400 hover:text-blue-300">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


